import { Box, Divider } from '@atgfr8/atg-ui-tailwind';
import SectionCard from '@mobileComponents/SectionCard';
import { customerDetailsStore } from '@mobileStore/customer';
import {
  StamperIcon,
  HelpCircleIcon,
  CreditCardIcon,
  CheckBoldIcon,
  CloseCircleOutlineIcon,
  TimerSandEmptyIcon,
} from '@atgfr8/icons';
import { formatDateOrNull } from '@utils/dateFormat';
import { formatAmount } from '@utils/utils';
import { For, Show } from 'solid-js';

const getIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'partiallyapproved':
      return <CheckBoldIcon size={24} />;
    case 'approved':
      return <StamperIcon size={24} />;
    case 'prepaidapproved':
      return <CreditCardIcon size={24} />;
    case 'denied':
      return <CloseCircleOutlineIcon size={24} />;
    case 'inreview':
      return <TimerSandEmptyIcon size={24} />;
    case 'pending':
      return <HelpCircleIcon size={24} />;
    default:
      return null;
  }
};

const iconColorMap: Record<string, string> = {
  partiallyapproved: '#689F38',
  approved: '#248670',
  prepaidapproved: '#FF8F00',
  denied: '#B00020',
  inreview: '#123B50',
  pending: '#7B4F13',
};

const RequestHistory = () => {
  const statusBgClassMap: Record<string, string> = {
    partiallyapproved: 'bg-[#d4e5f7]',
    approved: 'bg-[#d4f4dd]',
    prepaidapproved: 'bg-[#fff4d6]',
    denied: 'bg-[#fdd]',
    inreview: 'bg-[#b8d4e0]',
    pending: 'bg-[#fff4d6]',
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      partiallyapproved: 'Partial Approval By',
      approved: 'Request Approved By',
      prepaidapproved: 'Marked Prepay By',
      denied: 'Request Denied By',
      inreview: 'Marked In Review By',
      pending: 'Pending',
    };
    return statusMap[status.toLowerCase()] || status;
  };

  return (
    <Box class="m-[8px]">
      <SectionCard title="Request History">
        <For
          each={customerDetailsStore.customer.requests}
          fallback={
            <Box class="text-center py-6 text-gray-500 text-[16px]">
              No Requests Found
            </Box>
          }
        >
          {(request) => (
            <Box
              class={`${statusBgClassMap[request.status.toLowerCase()]} p-2! mx-2! mb-2`}
              style={{
                'border-radius': '4px',
                'box-shadow': '0 1px 3px rgba(0,0,0,0.12)',
              }}
            >
              <Box class="flex justify-between items-start">
                <span class="text-[14px]! font-semibold!">
                  {request.requestedByUserName}
                </span>
                <span class="text-[14px]!">
                  {formatDateOrNull(request.requestDate)}
                </span>
              </Box>

              <Box class="flex justify-between items-center mb-1">
                <span class="text-[14px]! font-semibold!">
                  Amount Requested
                </span>
                <span class="text-[14px]! font-semibold!">
                  {formatAmount(request.amount)}
                </span>
              </Box>

              <Show
                when={
                  request.status.toLowerCase() === 'approved' ||
                  request.status.toLowerCase() === 'partiallyapproved'
                }
              >
                <Box class="flex justify-between items-center mb-1">
                  <span class="text-[14px]! font-semibold!">
                    Amount Approved
                  </span>
                  <span class="text-[14px]! font-semibold!">
                    {request.approvedAmount != null
                      ? formatAmount(request.approvedAmount)
                      : 'N/A'}
                  </span>
                </Box>
              </Show>

              <Show when={request.details}>
                <p class="text-[14px]! mb-2 italic">{request.details}</p>
              </Show>

              <Box class="border-t border-gray-300 pt-2 mt-2">
                <Box class="flex justify-between items-center">
                  <Box class="flex items-center gap-2">
                    <Box
                      style={{
                        color:
                          iconColorMap[request.status.toLowerCase()] ||
                          '#000000',
                        display: 'flex',
                        'align-items': 'center',
                      }}
                    >
                      {getIcon(request.status)}
                    </Box>
                    <span class="text-[14px]! font-semibold!">
                      {getStatusLabel(request.status)}
                    </span>
                  </Box>
                  <Show when={request.approvedByUserName}>
                    <span class="text-[14px]!">
                      {request.approvedByUserName}
                    </span>
                  </Show>
                </Box>
              </Box>

              <Show when={request.comment}>
                <Box>
                  <Divider />
                  <p class="text-[14px]! mt-2 italic">{request.comment}</p>
                </Box>
              </Show>

              <Show when={request.reply}>
                <p class="text-[14px]! mt-1">Credit Comment: {request.reply}</p>
              </Show>
            </Box>
          )}
        </For>
      </SectionCard>
    </Box>
  );
};

export default RequestHistory;
